import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect, useRef, useMemo } from 'react'
import { Sparkles, Plus, Building2, Calendar, Users, ExternalLink, FileText, Trash2, Edit, ChevronDown, X, ArrowUpDown, ArrowUp, ArrowDown, Link2, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { PageSkeleton } from '@/components/ui/page-skeleton'
import { useAuth } from '@/contexts/AuthContext'
import { api } from '@/services/api'
import { CreateWorkshopModal, type WorkshopFormData } from '@/components/projects/CreateWorkshopModal'
import { EditWorkshopModal, type WorkshopFormData as EditWorkshopFormData } from '@/components/projects/EditWorkshopModal'
import { toTeamLinksPayload } from '@/components/projects/WorkshopTeamLinksField'
import { DeleteModal } from '@/components/ui/delete-modal'

interface WorkshopsSearch {
  workshop?: number
}

export const Route = createFileRoute('/workshops')({
  component: Workshops,
  validateSearch: (search: Record<string, unknown>): WorkshopsSearch => {
    const id = Number(search.workshop)
    return Number.isInteger(id) && id > 0 ? { workshop: id } : {}
  },
})

function workshopLink(workshopId: number): string {
  return `${window.location.origin}/workshops?workshop=${workshopId}`
}

async function copyText(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value)
      return true
    }
  } catch {
    // On retombe sur la copie manuelle ci-dessous.
  }
  const area = document.createElement('textarea')
  area.value = value
  area.setAttribute('readonly', '')
  area.style.position = 'fixed'
  area.style.opacity = '0'
  document.body.appendChild(area)
  area.select()
  const ok = document.execCommand('copy')
  document.body.removeChild(area)
  return ok
}

interface WorkshopURL {
  id: number
  url_type: string
  url: string
  created_at: string
}

interface Workshop {
  id: number
  description: string | null
  date: string
  consultants: string | null
  consultant_ids?: string[]
  prix: number | null
  client_id: string
  activity_sector_id: number | null
  created_at: string
  updated_at: string | null
  urls: WorkshopURL[]
  team_links?: Array<{ id: number; equipe: string; url: string; password: string | null }>
  visible_client?: boolean
  client_nom: string | null
  activity_sector_nom: string | null
}

interface WorkshopListResponse {
  data: Workshop[]
  count: number
  message: string
}

// Component for truncated description with tooltip
function TruncatedDescription({ description }: { description: string }) {
  const [showFullDescription, setShowFullDescription] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipPosition, setTooltipPosition] = useState<'top' | 'bottom'>('bottom')
  const descriptionRef = useRef<HTMLParagraphElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  // Check if text is truncated (more than 3 lines)
  useEffect(() => {
    if (descriptionRef.current) {
      const lineHeight = parseInt(getComputedStyle(descriptionRef.current).lineHeight)
      const maxHeight = lineHeight * 3
      const isTruncated = descriptionRef.current.scrollHeight > maxHeight
      setShowTooltip(isTruncated)
    }
  }, [description])

  // Calculate tooltip position based on available space
  useEffect(() => {
    if (showTooltip && descriptionRef.current && tooltipRef.current) {
      const rect = descriptionRef.current.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const spaceAbove = rect.top
      const tooltipHeight = 200 // Estimated tooltip height
      
      if (spaceBelow < tooltipHeight && spaceAbove > spaceBelow) {
        setTooltipPosition('top')
      } else {
        setTooltipPosition('bottom')
      }
    }
  }, [showTooltip])

  if (showFullDescription) {
    return (
      <div className="relative">
        <p className="text-sm text-gray-600 break-words whitespace-normal">
          {description}
        </p>
        <button
          onClick={() => setShowFullDescription(false)}
          className="mt-1 text-xs text-cyan-600 hover:text-cyan-700 flex items-center gap-1"
        >
          <X className="w-3 h-3" />
          Réduire
        </button>
      </div>
    )
  }

  return (
    <div className="relative">
      <p
        ref={descriptionRef}
        className="text-sm text-gray-600 break-words whitespace-normal line-clamp-3 cursor-pointer hover:text-gray-700 transition-colors relative group"
        onClick={() => setShowFullDescription(true)}
      >
        {description}
        {showTooltip && (
          <div
            ref={tooltipRef}
            className={`absolute z-50 opacity-0 group-hover:opacity-100 transition-all duration-200 ease-in-out ${
              tooltipPosition === 'top' 
                ? 'bottom-full mb-2' 
                : 'top-full mt-2'
            } left-0 right-0 max-w-xl`}
          >
            <div className="bg-white border border-gray-200 rounded-lg shadow-2xl p-4 transform group-hover:scale-100 scale-95 transition-transform duration-200">
              <div className="flex items-start justify-between gap-2 mb-2">
                <span className="text-xs font-semibold text-cyan-600 uppercase tracking-wide">
                  Description complète
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowFullDescription(true)
                  }}
                  className="text-xs text-cyan-600 hover:text-cyan-700 font-medium"
                >
                  Voir tout →
                </button>
              </div>
              <p className="text-sm text-gray-700 break-words whitespace-normal leading-relaxed">
                {description}
              </p>
              {/* Arrow */}
              <div
                className={`absolute left-6 w-2 h-2 bg-white border-r border-b border-gray-200 transform rotate-45 ${
                  tooltipPosition === 'top'
                    ? 'bottom-0 translate-y-1/2'
                    : 'top-0 -translate-y-1/2'
                }`}
              />
            </div>
          </div>
        )}
      </p>
    </div>
  )
}

// Component for URL dropdown button
function URLDropdownButton({ 
  urls, 
  label, 
  icon, 
  buttonClassName, 
  itemClassName 
}: { 
  urls: WorkshopURL[]
  label: string
  icon: React.ReactNode
  buttonClassName: string
  itemClassName: string
}) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition ${buttonClassName}`}
      >
        {icon}
        {label}
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        {urls.length > 1 && (
          <span className="ml-1 px-1.5 py-0.5 text-xs bg-white/50 rounded-full">
            {urls.length}
          </span>
        )}
      </button>
      
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[200px]">
          {urls.map((url, index) => (
            <a
              key={url.id}
              href={url.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`block px-4 py-2 text-sm text-gray-700 ${itemClassName} first:rounded-t-lg last:rounded-b-lg transition-colors`}
              onClick={() => setIsOpen(false)}
            >
              <div className="flex items-center gap-2">
                {icon}
                <span>
                  {urls.length > 1 ? `${label} ${index + 1}` : label}
                </span>
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  )
}

function Workshops() {
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin'
  const { workshop: workshopParam } = Route.useSearch()

  const [workshops, setWorkshops] = useState<Workshop[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingWorkshop, setEditingWorkshop] = useState<Workshop | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deletingWorkshop, setDeletingWorkshop] = useState<Workshop | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc')
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false)
  const sortDropdownRef = useRef<HTMLDivElement>(null)
  const [highlightedId, setHighlightedId] = useState<number | null>(null)
  const [copiedId, setCopiedId] = useState<number | null>(null)
  const [linkError, setLinkError] = useState('')
  const focusedFromUrl = useRef<number | null>(null)

  const fetchWorkshops = async () => {
    setLoading(true)
    try {
      const response = await api.get<WorkshopListResponse>('/workshops')
      const workshopsData = response.data?.data || []
      setWorkshops(workshopsData)
    } catch (error) {
      console.error('Erreur lors de la récupération des workshops:', error)
      setWorkshops([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchWorkshops()
  }, [])

  // Lien direct /workshops?workshop=ID : fait défiler jusqu'au workshop et le met en évidence.
  // Une seule fois par ID, pour ne pas re-scroller à chaque rechargement de la liste.
  useEffect(() => {
    if (!workshopParam) {
      focusedFromUrl.current = null
      return
    }
    if (loading || focusedFromUrl.current === workshopParam) return
    focusedFromUrl.current = workshopParam
    if (!workshops.some((w) => w.id === workshopParam)) {
      setLinkError('Workshop introuvable : il a peut-être été supprimé ou vous n’y avez pas accès.')
      return
    }
    setLinkError('')
    setHighlightedId(workshopParam)
    requestAnimationFrame(() => {
      document
        .getElementById(`workshop-${workshopParam}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })
    const timer = window.setTimeout(() => setHighlightedId(null), 4000)
    return () => window.clearTimeout(timer)
  }, [loading, workshopParam, workshops])

  const copyWorkshopLink = (workshopId: number) => {
    void copyText(workshopLink(workshopId)).then((ok) => {
      if (!ok) {
        alert('Copie impossible depuis ce navigateur.')
        return
      }
      setCopiedId(workshopId)
      window.setTimeout(() => setCopiedId((current) => (current === workshopId ? null : current)), 2000)
    })
  }

  // Trier les workshops par date
  const sortedWorkshops = useMemo(() => {
    return [...workshops].sort((a, b) => {
      const dateA = new Date(a.date).getTime()
      const dateB = new Date(b.date).getTime()
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB
    })
  }, [workshops, sortOrder])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target as Node)) {
        setSortDropdownOpen(false)
      }
    }

    if (sortDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [sortDropdownOpen])

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    const day = date.getDate()
    const monthNames = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']
    const month = monthNames[date.getMonth()]
    const year = date.getFullYear()
    return `${day} ${month} ${year}`
  }

  const handleCreate = async (data: WorkshopFormData) => {
    setIsCreating(true)
    try {
      const payload: any = {
        date: data.date,
        consultant_ids: data.consultantIds,
      }

      if (data.description?.trim()) {
        payload.description = data.description.trim()
      }
      if (data.urlsFigma && data.urlsFigma.length > 0) {
        payload.figma_urls = data.urlsFigma.filter(u => u && u.trim()).map(u => u.trim())
      }
      if (data.urlsProposition && data.urlsProposition.length > 0) {
        payload.proposition_urls = data.urlsProposition.filter(u => u && u.trim()).map(u => u.trim())
      }
      payload.team_links = toTeamLinksPayload(data.teamLinks)
      payload.visible_client = data.visibleClient ?? false
      if (data.prix !== undefined && data.prix !== null && data.prix !== '') {
        payload.prix = parseFloat(String(data.prix))
      }

      // Handle client
      if (data.clientId && data.clientNom) {
        const clientIdNumber = Number(data.clientId)
        payload.client_id = Number.isInteger(clientIdNumber) ? clientIdNumber : data.clientId
      } else if (data.clientName?.trim()) {
        const emails = data.clientEmails?.filter(e => e && e.trim()) || []
        if (emails.length > 0) {
          payload.client = {
            nom: data.clientName.trim(),
            emails: emails.map(e => e.trim())
          }
        } else if (data.clientEmail?.trim()) {
          payload.client = {
            nom: data.clientName.trim(),
            email: data.clientEmail.trim()
          }
        } else {
          throw new Error('Veuillez entrer au moins un email pour le client')
        }
      } else {
        throw new Error('Veuillez sélectionner ou créer un client')
      }

      // Handle activity sector
      if (data.activitySectorId && data.activitySectorId.trim() !== '') {
        const sectorId = parseInt(data.activitySectorId)
        if (!isNaN(sectorId) && sectorId > 0) {
          payload.activity_sector_id = sectorId
        }
      }

      await api.post('/workshops', payload)
      setShowCreateModal(false)
      await fetchWorkshops()
    } catch (error: any) {
      console.error('Erreur lors de la création du workshop:', error)
      let errorMessage = 'Une erreur s\'est produite lors de la création du workshop'
      if (error?.message) {
        errorMessage = error.message
      } else if (error?.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map((d: any) => {
            const field = Array.isArray(d.loc) ? d.loc.slice(-1)[0] : ''
            return field ? `${field}: ${d.msg || d.message || 'Erreur de validation'}` : (d.msg || d.message || 'Erreur de validation')
          }).join(', ')
        } else {
          errorMessage = error.response.data.detail
        }
      }
      alert(errorMessage)
    } finally {
      setIsCreating(false)
    }
  }

  const handleEdit = (workshop: Workshop) => {
    setEditingWorkshop(workshop)
    setShowEditModal(true)
  }

  const handleUpdate = async (data: EditWorkshopFormData) => {
    if (!editingWorkshop) return
    setIsUpdating(true)
    try {
      const payload: any = {}

      if (data.date) {
        payload.date = data.date
      }
      if (data.consultantIds !== undefined) {
        payload.consultant_ids = data.consultantIds
      }
      if (data.description !== undefined) {
        payload.description = data.description?.trim() || null
      }
      if (data.urlsFigma !== undefined) {
        payload.figma_urls = data.urlsFigma.filter(u => u && u.trim()).map(u => u.trim())
      }
      if (data.urlsProposition !== undefined) {
        payload.proposition_urls = data.urlsProposition.filter(u => u && u.trim()).map(u => u.trim())
      }
      if (data.teamLinks !== undefined) {
        payload.team_links = toTeamLinksPayload(data.teamLinks)
      }
      if (data.visibleClient !== undefined) {
        payload.visible_client = data.visibleClient
      }
      if (data.prix !== undefined && data.prix !== null && data.prix !== '') {
        payload.prix = parseFloat(String(data.prix))
      } else if (data.prix === '') {
        payload.prix = null
      }

      // Handle client
      if (data.clientId && String(data.clientId) !== String(editingWorkshop.client_id)) {
        const clientIdNumber = Number(data.clientId)
        payload.client_id = Number.isInteger(clientIdNumber) ? clientIdNumber : data.clientId
      }

      // Handle activity sector
      if (data.activitySectorId !== undefined) {
        if (data.activitySectorId && data.activitySectorId.trim() !== '') {
          const sectorId = parseInt(data.activitySectorId)
          if (!isNaN(sectorId) && sectorId > 0) {
            payload.activity_sector_id = sectorId
          }
        } else {
          payload.activity_sector_id = null
        }
      }

      await api.put(`/workshops/${editingWorkshop.id}`, payload)
      setShowEditModal(false)
      setEditingWorkshop(null)
      await fetchWorkshops()
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour du workshop:', error)
      let errorMessage = 'Une erreur s\'est produite lors de la mise à jour du workshop'
      if (error?.message) {
        errorMessage = error.message
      } else if (error?.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map((d: any) => {
            const field = Array.isArray(d.loc) ? d.loc.slice(-1)[0] : ''
            return field ? `${field}: ${d.msg || d.message || 'Erreur de validation'}` : (d.msg || d.message || 'Erreur de validation')
          }).join(', ')
        } else {
          errorMessage = error.response.data.detail
        }
      }
      alert(errorMessage)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleDeleteClick = (workshop: Workshop) => {
    setDeletingWorkshop(workshop)
    setShowDeleteModal(true)
  }

  const handleDeleteConfirm = async () => {
    if (!deletingWorkshop) return
    setIsDeleting(true)
    try {
      await api.delete(`/workshops/${deletingWorkshop.id}`)
      setShowDeleteModal(false)
      setDeletingWorkshop(null)
      await fetchWorkshops()
    } catch (error: any) {
      console.error('Erreur lors de la suppression du workshop:', error)
      alert('Une erreur s\'est produite lors de la suppression du workshop')
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div className="min-h-screen">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 flex items-center gap-2">
              Workshops
              <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-400" />
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Ateliers métiers et workshops réalisés avec nos clients
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex h-10 items-center rounded-full bg-gray-100 px-4 text-sm font-medium text-gray-600">
              {sortedWorkshops.length} workshop{sortedWorkshops.length > 1 ? 's' : ''}
            </span>
            <div className="relative" ref={sortDropdownRef}>
              <Button
                variant="outline"
                onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                className="bg-white text-gray-700 border-gray-200 hover:bg-gray-50 flex items-center gap-2 px-4"
              >
                <ArrowUpDown className="w-4 h-4" />
                {sortOrder === 'desc' ? 'Plus récents' : 'Plus anciens'}
                <ChevronDown className={`w-4 h-4 transition-transform ${sortDropdownOpen ? 'rotate-180' : ''}`} />
              </Button>
              {sortDropdownOpen && (
              <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[200px]">
                <button
                  onClick={() => {
                    setSortOrder('desc')
                    setSortDropdownOpen(false)
                  }}
                  className={`w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg transition-colors ${
                    sortOrder === 'desc' ? 'bg-cyan-50 text-cyan-700' : ''
                  }`}
                >
                  <ArrowDown className="w-4 h-4" />
                  Plus récents
                </button>
                <button
                  onClick={() => {
                    setSortOrder('asc')
                    setSortDropdownOpen(false)
                  }}
                  className={`w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg transition-colors ${
                    sortOrder === 'asc' ? 'bg-cyan-50 text-cyan-700' : ''
                  }`}
                >
                  <ArrowUp className="w-4 h-4" />
                  Plus anciens
                </button>
              </div>
              )}
            </div>
            {isAdmin && (
              <Button
                onClick={() => setShowCreateModal(true)}
                className="shrink-0 bg-gradient-to-r from-cyan-400 to-cyan-500 text-white hover:from-cyan-500 hover:to-cyan-600 shadow-md flex items-center gap-2 px-5"
              >
                <Plus className="w-5 h-5" />
                Nouveau Workshop
              </Button>
            )}
          </div>
        </div>

        {linkError && (
          <div className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <span>{linkError}</span>
            <button type="button" onClick={() => setLinkError('')} aria-label="Fermer">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Workshops List */}
        <div className="space-y-4">
          {loading ? (
            <PageSkeleton variant="list" />
          ) : sortedWorkshops.length === 0 ? (
            <div className="bg-white rounded-xl shadow-md p-8 text-center">
              <p className="text-gray-500 text-lg font-medium mb-2">Aucun workshop trouvé</p>
              <p className="text-sm text-gray-400">
                {isAdmin ? 'Créez votre premier workshop en cliquant sur le bouton "Nouveau Workshop"' : 'Aucun workshop disponible'}
              </p>
            </div>
          ) : (
            sortedWorkshops.map((workshop) => {
              const consultants = workshop.consultants ? workshop.consultants.split(',').map(c => c.trim()) : []
              
              return (
                <div
                  key={workshop.id}
                  id={`workshop-${workshop.id}`}
                  className={`bg-white rounded-xl border shadow-sm hover:shadow-md transition p-6 scroll-mt-24 ${
                    highlightedId === workshop.id ? 'border-cyan-400 ring-2 ring-cyan-300' : 'border-gray-100'
                  }`}
                >
                  <div className="flex flex-col gap-4">
                    {/* Header */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-cyan-50 flex-shrink-0">
                          <Building2 className="w-5 h-5 text-cyan-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-semibold text-gray-900 break-words whitespace-normal">
                            {workshop.client_nom || 'Client inconnu'}
                          </h3>
                          {workshop.activity_sector_nom && (
                            <p className="text-sm text-gray-500 mt-1 break-words whitespace-normal">{workshop.activity_sector_nom}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {workshop.date && (
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-cyan-50">
                              <Calendar className="w-4 h-4 text-cyan-600" />
                            </div>
                            <span>{formatDate(workshop.date)}</span>
                          </div>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyWorkshopLink(workshop.id)}
                          className="bg-white border-gray-200 hover:bg-gray-50"
                          title="Copier le lien du workshop"
                          aria-label="Copier le lien du workshop"
                        >
                          {copiedId === workshop.id ? (
                            <Check className="w-4 h-4 text-green-600" />
                          ) : (
                            <Link2 className="w-4 h-4" />
                          )}
                        </Button>
                        {isAdmin && (
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEdit(workshop)}
                              className="bg-white border-gray-200 hover:bg-gray-50"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteClick(workshop)}
                              className="bg-white border-red-200 hover:bg-red-50 text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Description */}
                    {workshop.description && (
                      <TruncatedDescription description={workshop.description} />
                    )}

                    {/* Meta infos */}
                    {consultants.length > 0 && (
                      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-cyan-50 flex-shrink-0">
                            <Users className="w-4 h-4 text-cyan-600" />
                          </div>
                          <span className="break-words whitespace-normal">
                            {consultants.length > 4 ? (
                              <>
                                {consultants.slice(0, 4).join(', ')} et {consultants.length - 4} autre{consultants.length - 4 > 1 ? 's' : ''}
                              </>
                            ) : (
                              consultants.join(', ')
                            )}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Actions - URLs */}
                    {workshop.urls && workshop.urls.length > 0 && (
                      <div className="flex items-center gap-3 pt-2 border-t border-gray-100 flex-wrap">
                        {/* Figma URLs */}
                        {(() => {
                          const figmaUrls = workshop.urls.filter(u => u.url_type === 'figma')
                          if (figmaUrls.length === 0) return null
                          
                          if (figmaUrls.length === 1) {
                            // Single URL - direct link
                            return (
                              <a
                                href={figmaUrls[0].url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-50 text-cyan-600 text-sm font-medium border border-cyan-200 hover:bg-cyan-100 transition"
                              >
                                <ExternalLink className="w-4 h-4" />
                                Voir le Figma
                              </a>
                            )
                          }
                          
                          // Multiple URLs - dropdown
                          return <URLDropdownButton
                            urls={figmaUrls}
                            label="Voir le Figma"
                            icon={<ExternalLink className="w-4 h-4" />}
                            buttonClassName="bg-cyan-50 text-cyan-600 border-cyan-200 hover:bg-cyan-100"
                            itemClassName="hover:bg-cyan-50"
                          />
                        })()}
                        
                        {/* Proposition URLs */}
                        {(() => {
                          const propositionUrls = workshop.urls.filter(u => u.url_type === 'proposition')
                          if (propositionUrls.length === 0) return null
                          
                          if (propositionUrls.length === 1) {
                            // Single URL - direct link
                            return (
                              <a
                                href={propositionUrls[0].url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-50 text-green-600 text-sm font-medium border border-green-200 hover:bg-green-100 transition"
                              >
                                <FileText className="w-4 h-4" />
                                Voir la proposition
                              </a>
                            )
                          }
                          
                          // Multiple URLs - dropdown
                          return <URLDropdownButton
                            urls={propositionUrls}
                            label="Voir la proposition"
                            icon={<FileText className="w-4 h-4" />}
                            buttonClassName="bg-green-50 text-green-600 border-green-200 hover:bg-green-100"
                            itemClassName="hover:bg-green-50"
                          />
                        })()}
                      </div>
                    )}
                  </div>
                </div>
              )
            })
          )}
        </div>
      </main>

      {/* Create Workshop Modal */}
      <CreateWorkshopModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        onSubmit={handleCreate}
        isLoading={isCreating}
      />

      {/* Edit Workshop Modal */}
      {editingWorkshop && (
        <EditWorkshopModal
          open={showEditModal}
          onOpenChange={(open) => {
            setShowEditModal(open)
            if (!open) setEditingWorkshop(null)
          }}
          onSubmit={handleUpdate}
          isLoading={isUpdating}
          workshop={editingWorkshop}
        />
      )}

      {/* Delete Modal */}
      <DeleteModal
        open={showDeleteModal}
        onOpenChange={setShowDeleteModal}
        onConfirm={handleDeleteConfirm}
        title="Supprimer le workshop"
        itemName={deletingWorkshop?.client_nom || 'ce workshop'}
        isLoading={isDeleting}
        description="Êtes-vous sûr de vouloir supprimer ce workshop ? Cette action est irréversible."
      />
    </div>
  )
}
